import React from "react";
import {
  Grid,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    padding: "10px",
    fontWeight: 700,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px",
    fontWeight: 400,
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

/**
 * CommonTable wraps TableContainer + Table + TableHead + optional Pagination.
 *
 * Props:
 *   columns          – array of column headers; each item can be a string/ReactNode
 *                      OR an object { label, ...TableCell props }; null/false is filtered out.
 *   children         – one or more <TableBody> elements (or any valid Table children).
 *   totalCount       – total record count; drives pagination visibility.
 *   currentPage      – current active page number.
 *   onPageChange     – (page: number) => void; required for pagination to render.
 *   itemsPerPage     – records per page, default 10.
 *   minWidth         – Table minWidth in px, default 500.
 *   ariaLabel        – Table aria-label, default "customized table".
 *   tableContainerClass – className for TableContainer.
 *   tableContainerSx – sx prop for TableContainer.
 *   paginationContainerProps – props spread on the Grid wrapping Pagination (default: { margin: 10 }).
 *   paginationProps  – additional props spread on the Pagination component.
 */
function CommonTable({
  columns = [],
  children,
  totalCount = 0,
  currentPage = 1,
  onPageChange,
  itemsPerPage = 10,
  minWidth = 500,
  ariaLabel = "customized table",
  tableContainerClass = "",
  tableContainerSx = {},
  paginationContainerProps = { margin: 10 },
  paginationProps = {},
}) {
  const filteredColumns = columns.filter(
    (col) => col !== null && col !== undefined && col !== false
  );

  return (
    <>
      <TableContainer component={Paper} className={tableContainerClass} sx={tableContainerSx}>
        <Table sx={{ minWidth }} aria-label={ariaLabel}>
          <TableHead>
            <TableRow className="bg-sky-tint">
              {filteredColumns.map((col, i) => {
                if (typeof col === "object" && col !== null && !React.isValidElement(col)) {
                  const { label, ...cellProps } = col;
                  return (
                    <StyledTableCell key={i} {...cellProps}>
                      {label}
                    </StyledTableCell>
                  );
                }
                return <StyledTableCell key={i}>{col}</StyledTableCell>;
              })}
            </TableRow>
          </TableHead>
          {children}
        </Table>
      </TableContainer>
      {totalCount > itemsPerPage && onPageChange && (
        <Grid item display="flex" justifyContent="center" {...paginationContainerProps}>
          <Pagination
            count={Math.ceil(totalCount / itemsPerPage)}
            page={currentPage}
            onChange={(_, page) => onPageChange(page)}
            variant="outlined"
            shape="rounded"
            showFirstButton
            showLastButton
            {...paginationProps}
          />
        </Grid>
      )}
    </>
  );
}

export default CommonTable;
